import React from "react";
import { Helmet } from "react-helmet";
import LanguageSelector from "./components/LanguageSelector";
import { trackEvent } from "./utils/metaPixel";
import { trackButtonClick } from "./utils/analytics";
import {
  FiHome,
  FiStar,
  FiDollarSign,
  FiFileText,
  FiPhone,
  FiTrendingUp,
  FiTarget,
  FiCpu,
  FiBarChart2,
  FiMessageCircle,
  FiGlobe,
  FiHelpCircle,
  FiClock,
  FiLayers,
  FiMapPin,
  FiSettings,
  FiShield,
} from "react-icons/fi";
import { useLazyLoad } from "./utils/performanceOptimizer";

// Custom CSS for animations
const customStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
  
  .animate-float.delay-1000 {
    animation-delay: 1s;
  }
  
  .animate-float.delay-2000 {
    animation-delay: 2s;
  }
  
  .animate-float.delay-3000 {
    animation-delay: 3s;
  }
  
  @keyframes gradient-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  .animate-gradient {
    background-size: 200% 200%;
    animation: gradient-shift 5s ease infinite;
  }
  
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.4); }
    50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.8); }
  }
  
  .animate-pulse-glow {
    animation: pulse-glow 3s ease-in-out infinite;
  }
  
  .glass-effect {
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  .text-shadow-strong {
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  .hover-lift {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .hover-lift:hover {
    transform: translateY(-8px) scale(1.02);
  }
  
  @media (max-width: 768px) {
    .text-4xl { font-size: 2.5rem; }
    .text-5xl { font-size: 3rem; }
    .text-6xl { font-size: 3.5rem; }
    .text-7xl { font-size: 4rem; }
  }
  
  .scroll-smooth {
    scroll-behavior: smooth;
  }
`;

// Add styles to head
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = customStyles;
  document.head.appendChild(styleSheet);
}

const features = [
  {
    icon: <FiTarget className="text-white text-3xl" />,
    title: "Dễ sử dụng – Không cần kỹ thuật",
    description: `Hỗ trợ định giá nhiều loại BĐS: căn hộ, nhà phố, đất nền, biệt thự và các loại tài sản khác`,
  },
  {
    icon: <FiFileText className="text-white text-3xl" />,
    title: "Tốc độ định giá ngay tức thì",
    description:
      "Form thông minh với validation real-time, auto-save dữ liệu và giao diện responsive.",
  },
  {
    icon: <FiCpu className="text-white text-3xl" />,
    title: "Cập nhật dữ liệu liên tục từ thị trường",
    description:
      "Công nghệ AI tiên tiến phân tích real-time với WebSocket tracking và độ chính xác cao.",
  },
  {
    icon: <FiBarChart2 className="text-white text-3xl" />,
    title: "So sánh mức giá chi tiết theo khu vực",
    description:
      "Hiển thị giá ước tính, các yếu tố ảnh hưởng và xuất báo cáo chuyên nghiệp.",
  },
  {
    icon: <FiHome className="text-white text-3xl" />,
    title: "Định giá cả bán và cho thuê",
    description:
      "Hỗ trợ đánh giá giá trị cho cả mục đích bán và cho thuê bất động sản một cách linh hoạt.",
  },
];

const targetUsers = [
  "Môi giới bất động sản - Định giá nhanh cho khách hàng",
  "Nhà đầu tư - Đánh giá giá trị tài sản trước khi mua/bán",
  "Chủ sở hữu BĐS - Xác định giá trị tài sản hiện tại",
  "Chuyên gia thẩm định - Hỗ trợ công việc thẩm định giá",
  "Doanh nghiệp BĐS - Tối ưu hóa quy trình định giá",
];

const companyInfo = [
  "CT Global Holdings là tập đoàn được thành lập ngày 10/10/2019 tại Singapore, giấy phép kinh doanh số 201934143N và có địa chỉ tại 160 Robinson Road, #14-04, Singapore (068914). Tập đoàn hoạt động chính trong các lĩnh vực đầu tư, công nghệ ứng dụng và bất động sản.",
  "Tại Việt Nam, công ty TNHH CT Toàn Cầu (công ty 100% thuộc CT Global Holdings), mã số thuế 0108591862, đăng kí kinh doanh tại địa chỉ tòa Hồ Gươm Plaza, số 102 Trần Phú, Hà Đông, Hà Nội.",
  "Công ty là chủ sở hữu thương hiệu và nền tảng YouHomes, ứng dụng cung cấp dịch vụ toàn diện, được tích hợp AI và bigdata, bao gồm:",
  "+ Hệ thống quản trị tuỳ biến theo doanh nghiệp (White-labeled Operating System), có tên thương mại là bPRO, giúp tối ưu hóa công việc quản trị vận hành với bộ ứng dụng quản lý khách hàng CRM, quản lý danh mục bất động sản IMS…",
  "+ Trung tâm AI (AICenter) giúp tự động hóa công việc Marketing, tăng X10 hiệu quả thu hút và tiếp cận khách hàng với trọn bộ công cụ AI mà người kinh doanh bán lẻ cần có như công cụ post bài hàng loạt & tự động tìm khách trên facebook (AIToolFB), công cụ tạo video tức thời (AIVideo), tạo nội dung tự động (AIContent), định giá bất động sản thông minh (AIPrice)…",
  "+ Trung tâm dữ liệu lớn (Bigdata Powerhouse) giúp người dùng khai thác và vận dụng dữ liệu thị trường bất động sản nhằm tối ưu hoạt động kinh doanh và đầu tư",
  "Ngoài ra, công ty cũng hoạt động trong các lĩnh vực đầu tư, phát triển dự án và xây dựng, kinh doanh sản phẩm phần mềm, ứng dụng công nghệ AI.",
];

const contactInfo = [
  {
    label: "Hotline",
    value: "0886399781",
    icon: <FiPhone className="text-blue-500 text-xl" />,
  },
  {
    label: "Email",
    value: "contact@youhomes.vn",
    icon: <FiMessageCircle className="text-blue-500 text-xl" />,
  },
  {
    label: "Website",
    value: "https://youhomes.vn/aiprice",
    icon: <FiGlobe className="text-blue-500 text-xl" />,
  },
  {
    label: "Zalo",
    value: "0886399781",
    icon: (
      <svg
        className="text-blue-500 w-5 h-5"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M12.49 10.272v-.45h1.347v6.322h-1.347v-.453c0-.016 0-.032-.003-.048a.171.171 0 00-.028-.086.208.208 0 00-.068-.07c-.384-.242-.816-.414-1.263-.54a6.554 6.554 0 00-1.938-.263 6.725 6.725 0 00-2.14.343c-.696.227-1.336.57-1.87 1.03a4.797 4.797 0 00-1.258 1.59c-.298.608-.434 1.264-.425 1.944.009.69.155 1.35.465 1.96a4.877 4.877 0 001.303 1.577c.558.45 1.193.783 1.883 1.001.68.217 1.394.322 2.116.322.803 0 1.591-.134 2.346-.398a6.236 6.236 0 002.1-1.235l.96 1.034a8.71 8.71 0 01-1.333.826 7.426 7.426 0 01-1.439.54 8.602 8.602 0 01-1.504.263 10.875 10.875 0 01-1.508.033 7.437 7.437 0 01-2.535-.494 6.55 6.55 0 01-2.161-1.4 6.722 6.722 0 01-1.513-2.145 6.424 6.424 0 01-.455-4.878 6.452 6.452 0 011.492-2.147 7.34 7.34 0 012.284-1.43 7.785 7.785 0 012.798-.494c.324 0 .656.019.99.058.329.038.654.098.97.178.314.08.618.184.91.31.292.124.561.274.814.45a.224.224 0 00.089.026.114.114 0 00.07-.026.171.171 0 00.052-.066.192.192 0 00.02-.089zm-1.337 2.304c-.325-.203-.695-.351-1.095-.45a5.25 5.25 0 00-1.26-.142c-.696 0-1.336.115-1.883.351a3.753 3.753 0 00-1.373.941c-.371.396-.66.853-.85 1.359-.193.505-.285 1.042-.285 1.597 0 .536.092 1.055.285 1.542.19.488.47.928.841 1.306.371.378.825.681 1.347.898.522.217 1.115.33 1.765.33.487 0 .946-.066 1.364-.196.417-.13.79-.312 1.115-.54.324-.23.594-.505.805-.816.21-.312.337-.653.37-1.017h1.346a4.4 4.4 0 01-.493 1.587 4.08 4.08 0 01-1.08 1.288c-.461.366-1.006.648-1.624.844-.618.196-1.301.297-2.042.297-.867 0-1.661-.142-2.364-.423a5.493 5.493 0 01-1.856-1.172 5.211 5.211 0 01-1.213-1.79c-.29-.687-.432-1.434-.432-2.233 0-.79.142-1.527.432-2.205.288-.678.687-1.27 1.195-1.764a5.616 5.616 0 011.847-1.19c.72-.29 1.513-.437 2.364-.437.487 0 .961.049 1.418.15.456.1.88.246 1.266.432.387.185.739.41 1.054.673.315.262.576.55.779.862l-1.328.755a3.56 3.56 0 00-.761-.755z" />
      </svg>
    ),
  },
  {
    label: "Facebook",
    value: "https://facebook.com/youhomes.vn",
    icon: (
      <svg
        className="text-blue-500 w-5 h-5"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
      </svg>
    ),
  },
];

const problemPoints = [
  {
    icon: <FiHelpCircle className="text-red-500 text-3xl" />,
    title: "Định giá BĐS thiếu chính xác",
    description:
      "Ước lượng giá dựa trên kinh nghiệm, thiếu dữ liệu khách quan và phân tích chuyên sâu",
  },
  {
    icon: <FiClock className="text-red-500 text-3xl" />,
    title: "Quy trình định giá lâu",
    description:
      "Mất nhiều thời gian thu thập thông tin, phân tích thủ công và tạo báo cáo",
  },
  {
    icon: <FiDollarSign className="text-red-500 text-3xl" />,
    title: "Chi phí thẩm định cao",
    description:
      "Thuê chuyên gia thẩm định giá tốn kém, đặc biệt với các giao dịch nhỏ",
  },
  {
    icon: <FiLayers className="text-red-500 text-3xl" />,
    title: "Thiếu dữ liệu thị trường",
    description:
      "Khó tiếp cận thông tin giá cả thị trường real-time và xu hướng",
  },
  {
    icon: <FiMapPin className="text-red-500 text-3xl" />,
    title: "Không đồng nhất về giá",
    description:
      "Các nguồn định giá khác nhau cho kết quả chênh lệch lớn, gây khó khăn trong quyết định",
  },
  {
    icon: <FiSettings className="text-red-500 text-3xl" />,
    title: "Thiếu công cụ chuyên nghiệp",
    description:
      "Các công cụ định giá hiện tại phức tạp, khó sử dụng hoặc không phù hợp với thị trường Việt Nam",
  },
];

const solutionPoints = [
  {
    icon: <FiCpu className="text-white text-3xl" />,
    title: "AI Định Giá Thông Minh",
    description:
      "Công nghệ AI tiên tiến phân tích hàng nghìn yếu tố để đưa ra giá ước tính chính xác, khách quan và đáng tin cậy",
  },
  {
    icon: <FiClock className="text-white text-3xl" />,
    title: "Định giá hiệu quả",
    description:
      "Quy trình tự động hóa giúp có kết quả định giá chi tiết, tiết kiệm thời gian và tăng hiệu quả công việc",
  },
  {
    icon: <FiBarChart2 className="text-white text-3xl" />,
    title: "Báo cáo chuyên nghiệp",
    description:
      "Báo cáo định giá chi tiết với phân tích các yếu tố ảnh hưởng, độ tin cậy và xu hướng thị trường",
  },
];

const testimonials = [
  {
    quote:
      "AIPrice đã thay đổi hoàn toàn cách chúng tôi định giá bất động sản. Từ việc mất cả ngày để thẩm định một căn nhà, giờ chỉ cần 5 phút là có kết quả chính xác. Khách hàng rất hài lòng với độ tin cậy của báo cáo.",
    name: "Nguyễn Văn Minh",
    position: "Giám đốc, Công ty BĐS Minh Phát",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
  },
  {
    quote:
      "Với AIPrice, chúng tôi có thể đưa ra mức giá chính xác ngay trong buổi tư vấn đầu tiên. Điều này giúp tăng tỷ lệ thành công lên 40% và khách hàng tin tưởng hơn vào chuyên môn của chúng tôi.",
    name: "Trần Thị Thu Hà",
    position: "Môi giới trưởng, Century 21",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg",
  },
  {
    quote:
      "Là một nhà đầu tư, tôi cần đánh giá nhanh nhiều bất động sản. AIPrice giúp tôi tiết kiệm hàng triệu đồng chi phí thẩm định và đưa ra quyết định đầu tư chính xác hơn.",
    name: "Lê Hoàng Nam",
    position: "Nhà đầu tư BĐS",
    avatar: "https://randomuser.me/api/portraits/men/67.jpg",
  },
  {
    quote:
      "Công cụ này thực sự hữu ích cho công việc thẩm định của chúng tôi. Kết quả từ AIPrice rất gần với giá thị trường thực tế, giúp chúng tôi có thêm dữ liệu tham khảo đáng tin cậy.",
    name: "Phạm Minh Tuấn",
    position: "Chuyên gia thẩm định giá",
    avatar: "https://randomuser.me/api/portraits/men/23.jpg",
  },
  {
    quote:
      "AIPrice giúp team môi giới của chúng tôi tự tin hơn khi tư vấn khách hàng. Báo cáo chi tiết và chuyên nghiệp tạo ấn tượng tốt và nâng cao uy tín công ty.",
    name: "Võ Thị Lan Anh",
    position: "Trưởng phòng kinh doanh, BĐS Hoàng Gia",
    avatar: "https://randomuser.me/api/portraits/women/15.jpg",
  },
  {
    quote:
      "Từ khi sử dụng AIPrice, chúng tôi giảm được 70% thời gian định giá và tăng 50% số lượng khách hàng có thể phục vụ mỗi ngày. ROI của công cụ này rất ấn tượng.",
    name: "Đỗ Văn Thành",
    position: "Founder, reviewnha.com",
    avatar: "https://randomuser.me/api/portraits/men/65.jpg",
  },
];

const passiveIncomeFeatures = [
  "Định giá tự động bằng AI",
  "Phân tích real-time với WebSocket",
  "Báo cáo chuyên nghiệp tức thời",
  "Lưu trữ và quản lý lịch sử định giá",
  "Tích hợp dữ liệu thị trường",
  "Hỗ trợ đa loại bất động sản",
];

const SEO = () => (
  <Helmet>
    <html lang="vi" />
    <title>AIPrice – Công cụ định giá bất động sản tự động bằng AI</title>
    <meta
      name="description"
      content="AIPrice là công cụ AI hỗ trợ định giá bất động sản nhanh chóng và chính xác chỉ trong vài phút – không cần chuyên gia thẩm định giá."
    />
    <meta
      name="keywords"
      content="AIPrice, AI định giá BĐS, định giá bất động sản, thẩm định giá, AI cho bất động sản"
    />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
  </Helmet>
);

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const handleTryNow = React.useCallback(() => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "header" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_header");

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/ai-price";
    } else {
      window.location.href = "/login?returnUrl=/ai-price";
    }
  }, []);

  const menuItems = [
    { name: "Trang Chủ", icon: <FiHome className="mr-2" />, href: "#" },
    { name: "Tính Năng", icon: <FiStar className="mr-2" />, href: "#features" },
    {
      name: "Khách Hàng",
      icon: <FiStar className="mr-2" />,
      href: "#testimonials",
    },
    { name: "Liên Hệ", icon: <FiPhone className="mr-2" />, href: "#contact" },
  ];

  return (
    <header className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white py-3 shadow-2xl sticky top-0 z-50 backdrop-blur-sm border-b border-blue-800/30">
      <div className="container mx-auto flex justify-between items-center px-4 lg:px-8">
        <a
          href="/"
          className="flex items-center gap-4 font-bold text-2xl group"
        >
          <div className="relative">
            <img
              src={require("./assets/images/aiprice-logo.png")}
              alt="AIPrice Logo"
              className="h-14 w-auto border-2 border-blue-400 rounded-xl shadow-lg group-hover:border-yellow-400 transition-all duration-300"
              loading="lazy"
            />
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-300"></div>
          </div>
          <div className="flex flex-col">
            <span className="text-2xl xl:text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-200 bg-clip-text text-transparent">
              AIPrice
            </span>
            <span className="text-xs text-blue-300 font-medium -mt-1">
              Định giá BĐS thông minh
            </span>
          </div>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          <nav>
            <ul className="flex space-x-6 xl:space-x-8">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <a
                    href={item.href}
                    className="hover:text-yellow-300 transition-all duration-300 flex items-center text-sm xl:text-base font-medium group relative"
                  >
                    <span className="relative z-10">{item.name}</span>
                    <div className="absolute inset-0 bg-blue-700/50 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 -z-0 scale-95 group-hover:scale-100"></div>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-4">
            <LanguageSelector key="landing-desktop-lang" isInHeader={true} />
            <button
              onClick={handleTryNow}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 rounded-full font-bold hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 text-sm xl:text-base shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border border-yellow-300"
            >
              <span className="flex items-center gap-2">
                <FiTrendingUp className="text-sm" />
                Định giá ngay
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-4">
          <LanguageSelector key="landing-mobile-lang" isInHeader={true} />
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white focus:outline-none bg-blue-800/50 p-2 rounded-lg border border-blue-700/50"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-gradient-to-b from-blue-900 to-slate-900 mt-2 py-6 px-4 border-t border-blue-800/50">
          <nav>
            <ul className="space-y-4">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <a
                    href={item.href}
                    className="hover:text-yellow-300 transition-all duration-300 flex items-center text-base font-medium py-3 px-4 rounded-lg hover:bg-blue-800/50 border border-transparent hover:border-blue-700/50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.icon}
                    {item.name}
                  </a>
                </li>
              ))}
              <li className="pt-4 mt-4 border-t border-blue-800/50">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleTryNow();
                  }}
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-3 rounded-full font-bold hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 text-base shadow-lg"
                >
                  <span className="flex items-center justify-center gap-2">
                    <FiTrendingUp className="text-sm" />
                    Định giá ngay
                  </span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

const HeroSection = () => {
  const handleTryNow = () => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "hero_section" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_button", { location: "hero_section" });

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/ai-price";
    } else {
      window.location.href = "/login?returnUrl=/ai-price";
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-20 lg:py-32">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-yellow-400/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      </div>

      {/* Floating Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 animate-bounce delay-1000">
          <FiHome className="text-blue-300/30 text-3xl" />
        </div>
        <div className="absolute top-40 right-20 animate-bounce delay-2000">
          <FiTrendingUp className="text-yellow-300/30 text-2xl" />
        </div>
        <div className="absolute bottom-32 left-20 animate-bounce delay-3000">
          <FiMapPin className="text-green-300/30 text-2xl" />
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="space-y-8 text-center lg:text-left">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500/20 to-blue-500/20 px-6 py-3 rounded-full border border-green-400/30 backdrop-blur-sm">
              <FiShield className="text-green-400 text-lg" />
              <span className="text-sm font-medium">
                Được tin dùng bởi 200+ doanh nghiệp BĐS
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight">
              <span className="block text-white mb-2">
                AIPrice - Công cụ định giá bất động sản
              </span>
              <span className="block bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 bg-clip-text text-transparent mb-4">
                bằng AI chính xác
              </span>
              <span className="block text-2xl lg:text-3xl xl:text-4xl text-blue-200 font-medium">
                theo thời gian thực
              </span>
            </h1>

            <p className="text-lg lg:text-xl xl:text-2xl text-blue-100 leading-relaxed max-w-2xl">
              <span className="font-semibold text-white">AIPrice</span> - Công
              cụ AI tiên tiến giúp bạn định giá bất động sản
              <span className="text-yellow-300 font-semibold">
                {" "}
                chính xác tới 95%
              </span>
              , tiết kiệm thời gian và chi phí thẩm định đáng kể.
            </p>

            {/* Value Propositions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-6">
              <div className="bg-white/5 backdrop-blur-sm px-4 py-4 rounded-xl border border-white/10 text-center">
                <div className="text-2xl lg:text-3xl font-bold text-yellow-400 mb-1">
                  +15,000
                </div>
                <div className="text-blue-200 text-sm font-medium">
                  Người dùng tin tưởng
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm px-4 py-4 rounded-xl border border-white/10 text-center">
                <div className="text-2xl lg:text-3xl font-bold text-green-400 mb-1">
                  95%
                </div>
                <div className="text-blue-200 text-sm font-medium">
                  Độ chính xác
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm px-4 py-4 rounded-xl border border-white/10 text-center">
                <div className="text-2xl lg:text-3xl font-bold text-blue-400 mb-1">
                  24/7
                </div>
                <div className="text-blue-200 text-sm font-medium">
                  Hỗ trợ liên tục
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                onClick={handleTryNow}
                className="group bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:from-yellow-300 hover:to-yellow-400 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-yellow-400/25"
              >
                <span className="flex items-center justify-center gap-3">
                  <FiTrendingUp className="text-xl group-hover:scale-110 transition-transform" />
                  Định giá ngay
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              {/* <button className="group bg-transparent border-2 border-blue-400 text-blue-300 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-blue-400 hover:text-white transition-all duration-300">
                <span className="flex items-center justify-center gap-3">
                  <FiFileText className="text-lg group-hover:scale-110 transition-transform" />
                  Xem demo sản phẩm
                </span>
              </button> */}
            </div>

            {/* Security & Trust */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 pt-8 text-sm text-blue-300">
              <div className="flex items-center gap-2">
                <FiShield className="text-green-400" />
                <span>Bảo mật SSL 256-bit</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex text-yellow-400">
                  {"★★★★★".split("").map((star, i) => (
                    <span key={i} className="text-sm">
                      {star}
                    </span>
                  ))}
                </div>
                <span>4.9/5 từ 1,200+ đánh giá</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative w-[90%] mx-auto">
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-2xl">
              <img
                src={require("./assets/images/aiprice-banner.png")}
                alt="Giao diện AIPrice - Định giá bất động sản thông minh"
                className="rounded-2xl shadow-2xl w-full transform hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />

              {/* Floating Elements */}
              <div className="absolute -top-6 -right-6 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-pulse">
                95% Chính xác
              </div>
              <div className="absolute -bottom-4 -left-4 bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                AI Powered
              </div>
            </div>

            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl blur-3xl -z-10 scale-110"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProblemSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);

  return (
    <section
      id="problems"
      ref={ref}
      className="relative bg-gradient-to-b from-gray-50 to-white py-20"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-red-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-orange-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 bg-red-50 px-6 py-3 rounded-full border border-red-200 mb-6">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-red-700 font-semibold text-sm">
              Thực trạng ngành BĐS hiện tại
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold mb-6">
            <span className="text-gray-900">Bạn Có Đang Gặp</span>
            <br />
            <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
              Những Khó Khăn Này?
            </span>
          </h2>

          <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            <span className="font-semibold text-red-600">
              98% chủ nhà và 87% nhân viên BĐS
            </span>{" "}
            đang phải đối mặt với những thách thức nghiêm trọng trong việc định
            giá bất động sản chính xác và hiệu quả
          </p>

          {/* Impact Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12 max-w-4xl mx-auto">
            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="text-3xl font-bold text-red-600 mb-2">
                2-7 ngày
              </div>
              <div className="text-red-700 font-medium">
                Thời gian định giá truyền thống
              </div>
            </div>
            <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
              <div className="text-3xl font-bold text-orange-600 mb-2">
                5-15 triệu
              </div>
              <div className="text-orange-700 font-medium">
                Chi phí thẩm định mỗi lần
              </div>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
              <div className="text-3xl font-bold text-yellow-600 mb-2">
                ±20%
              </div>
              <div className="text-yellow-700 font-medium">
                Sai số định giá thông thường
              </div>
            </div>
          </div>
        </div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {problemPoints.map((problem, idx) => (
            <div
              key={idx}
              className={`group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform border border-gray-100 hover:border-red-200 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              <div className="flex flex-col items-center text-center space-y-6">
                {/* Icon */}
                <div className="relative">
                  <div className="bg-gradient-to-br from-red-100 to-red-50 p-6 rounded-2xl group-hover:from-red-200 group-hover:to-red-100 transition-all duration-300">
                    <div className="transform group-hover:scale-110 transition-transform duration-300">
                      {problem.icon}
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-red-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 scale-110"></div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 group-hover:text-red-700 transition-colors duration-300">
                    {problem.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-base lg:text-lg">
                    {problem.description}
                  </p>
                </div>

                {/* Impact Indicator */}
                <div className="w-full bg-red-50 p-4 rounded-xl border border-red-100">
                  <div className="flex items-center justify-center gap-2 text-red-700">
                    <FiTrendingUp className="text-red-500" />
                    <span className="text-sm font-medium">
                      {idx === 0 && "Ảnh hưởng tới uy tín"}
                      {idx === 1 && "Giảm hiệu quả kinh doanh"}
                      {idx === 2 && "Tăng chi phí vận hành"}
                      {idx === 3 && "Quyết định sai lầm"}
                      {idx === 4 && "Mất cơ hội giao dịch"}
                      {idx === 5 && "Lạc hậu trong cạnh tranh"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 lg:p-12 rounded-3xl border border-red-200 max-w-4xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Đã đến lúc thay đổi cách bạn định giá BĐS!
            </h3>
            <p className="text-lg text-gray-700 mb-8">
              Hãy để AIPrice giải quyết tất cả những khó khăn trên một cách
              nhanh chóng và hiệu quả
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() =>
                  document
                    .getElementById("solutions")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Xem giải pháp ngay →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SolutionSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);

  return (
    <section
      id="solutions"
      ref={ref}
      className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16"
    >
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          Vì sao bạn nên sử dụng AIPrice?
        </h2>
        <p className="text-center text-blue-100 max-w-3xl mx-auto mb-12">
          AIPrice cung cấp giải pháp toàn diện giúp tự động hoá hoàn toàn quy
          trình định giá bất động sản chính xác và nhanh chóng
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {solutionPoints.map((solution, idx) => (
            <div
              key={idx}
              className={`bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="bg-blue-500/30 p-4 rounded-full mb-6 border border-blue-300/30">
                  {solution.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{solution.title}</h3>
                <p className="text-blue-100">{solution.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <a
            href="#features"
            className="inline-block bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-50 transition"
          >
            Xem Chi Tiết
          </a>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);

  return (
    <section
      id="features"
      ref={ref}
      className="relative bg-gradient-to-b from-white to-blue-50 py-24 overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-green-500/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
      </div>

      {/* Floating Tech Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-32 left-20 animate-float">
          <FiCpu className="text-blue-300/20 text-4xl" />
        </div>
        <div className="absolute top-64 right-32 animate-float delay-1000">
          <FiBarChart2 className="text-green-300/20 text-3xl" />
        </div>
        <div className="absolute bottom-40 left-32 animate-float delay-2000">
          <FiTarget className="text-yellow-300/20 text-3xl" />
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 bg-blue-50 px-6 py-3 rounded-full border border-blue-200 mb-6">
            <FiCpu className="text-blue-600 text-lg" />
            <span className="text-blue-700 font-semibold text-sm">
              Công nghệ AI tiên tiến
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold mb-6">
            <span className="text-gray-900">Tính năng nổi bật</span>
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              của AIPrice
            </span>
          </h2>

          <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Khám phá hệ sinh thái công cụ AI mạnh mẽ giúp bạn
            <span className="font-semibold text-blue-600">
              {" "}
              cách mạng hóa
            </span>{" "}
            quy trình định giá bất động sản
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-8">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className={`group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform border border-gray-100 hover:border-blue-200 hover:-translate-y-2 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              {/* Icon Container */}
              <div className="relative mb-8">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-blue-200 group-hover:shadow-blue-300 group-hover:scale-110 transition-all duration-300">
                  <div className="transform group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                </div>
                {/* Floating Number */}
                <div className="absolute -top-3 -right-3 bg-yellow-400 text-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                  {idx + 1}
                </div>
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 scale-110"></div>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300 leading-tight">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-base group-hover:text-gray-700 transition-colors duration-300">
                  {feature.description}
                </p>
              </div>

              {/* Feature Benefits */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center gap-2 text-green-600">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium">
                    {idx === 0 && "Hỗ trợ 15+ loại BĐS"}
                    {idx === 1 && "Auto-save mọi thao tác"}
                    {idx === 2 && "Độ chính xác 95%+"}
                    {idx === 3 && "Export báo cáo chi tiết"}
                    {idx === 4 && "Chuyên nghiệp, hiệu quả"}
                  </span>
                </div>
              </div>

              {/* Hover Arrow */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-4 flex justify-center">
                <div className="bg-blue-100 p-2 rounded-full">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Process Flow */}
        <div className="mt-20">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Quy Trình Định Giá <span className="text-blue-600">Chỉ 3 Bước</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connection Lines */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-600 -translate-y-1/2 z-0"></div>

            {[
              { step: 1, label: "Nhập thông tin" },
              { step: 2, label: "AI phân tích" },
              { step: 3, label: "Nhận báo cáo" },
            ].map((item, idx) => (
              <div key={idx} className="relative z-10 text-center">
                <div className="bg-white border-4 border-blue-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600 font-bold text-xl shadow-lg">
                  {item.step}
                </div>
                <div className="bg-white p-4 rounded-xl shadow-md border border-blue-100">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Bước {item.step}
                  </h4>
                  <p className="text-sm text-gray-600">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 lg:p-12 rounded-3xl border border-blue-200 max-w-4xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              Sẵn sàng trải nghiệm công nghệ AI định giá BĐS?
            </h3>
            <p className="text-lg text-gray-700 mb-8">
              Hơn 5,000 chuyên gia BĐS đã tin tưởng và sử dụng AIPrice
            </p>
            <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              Khám phá ngay →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const TargetUsersSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);

  return (
    <section
      id="target-users"
      ref={ref}
      className="bg-gradient-to-r from-blue-100 to-blue-50 py-16"
    >
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-blue-800">
          Dành cho ai?
        </h2>
        <ul className="max-w-2xl mx-auto space-y-4 text-lg">
          {targetUsers.map((user, idx) => (
            <li
              key={idx}
              className={`flex items-center gap-3 transform transition-all duration-300 ${
                isVisible
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-10"
              }`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <span className="inline-block w-3 h-3 bg-blue-500 rounded-full"></span>
              {user}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

// eslint-disable-next-line no-unused-vars
const ClientLogos = () => (
  <div className="py-8 bg-white">
    <div className="container mx-auto px-4">
      <p className="text-center text-gray-500 mb-6 text-sm font-medium uppercase tracking-wider">
        Được tin dùng bởi các doanh nghiệp hàng đầu
      </p>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
        {/* Placeholder logos - Thay thế bằng logo thực tế */}
        <div className="w-24 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 font-semibold">
          Logo 1
        </div>
        <div className="w-24 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 font-semibold">
          Logo 2
        </div>
        <div className="w-24 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 font-semibold">
          Logo 3
        </div>
        <div className="w-24 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 font-semibold">
          Logo 4
        </div>
        <div className="w-24 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 font-semibold">
          Logo 5
        </div>
      </div>
    </div>
  </div>
);

const TestimonialsSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);

  return (
    <section id="testimonials" ref={ref} className="bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-blue-700">
          Khách Hàng Nói Gì Về Chúng Tôi?
        </h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
          +200 doanh nghiệp BĐS và +15.000 người dùng đã tin tưởng và sử dụng
          AIPrice để định giá bất động sản thông minh
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {testimonials.map((testimonial, idx) => (
            <div
              key={idx}
              className={`bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <svg
                    className="w-8 h-8 text-blue-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>

                <p className="text-gray-700 mb-6 flex-grow">
                  "{testimonial.quote}"
                </p>

                <div className="flex flex-col sm:flex-row items-center sm:items-start mt-auto">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mb-3 sm:mb-0 sm:mr-4 object-cover"
                    loading="lazy"
                  />
                  <div className="text-center sm:text-left">
                    <h4 className="font-bold text-gray-800">
                      {testimonial.name}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {testimonial.position}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProductivitySection = () => {
  const [ref] = useLazyLoad(0.1);

  const handleTryNow = () => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "productivity_section" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_button", { location: "productivity_section" });

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/ai-price";
    } else {
      window.location.href = "/login?returnUrl=/ai-price";
    }
  };

  return (
    <section id="productivity" ref={ref} className="bg-white py-12">
      <div className="container mx-auto px-4 text-center max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-700">
          Lợi ích vượt trội dành cho môi giới, chủ nhà và nhà đầu tư
        </h2>
        <p className="text-lg mb-4">
          Bạn không cần thuê chuyên gia thẩm định. Bạn không cần mất nhiều thời
          gian nghiên cứu thị trường.
          <br />
          <span className="font-semibold">
            Bạn chỉ cần AIPrice để định giá bất động sản: Tự động – Nhanh chóng
            – Chính xác.
          </span>
        </p>
        <p className="text-base text-gray-700">
          Bắt đầu định giá bất động sản ngay bây giờ!
        </p>

        <p className="mt-3 text-base text-gray-700 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          🎁 ĐỊNH GIÁ NGAY và hãy để AIPrice phân tích giá trị bất động sản của
          bạn một cách chính xác nhất, giúp đưa ra quyết định mua bán thông minh
          và tối ưu lợi nhuận.
          <br />
          <br />
          👉 Truy cập tại{" "}
          <a
            href="/ai-price"
            className="text-blue-600 font-medium hover:underline"
          >
            AIPrice
          </a>{" "}
          hoặc liên hệ hotline{" "}
          <a
            href="tel:0886399781"
            className="text-blue-600 font-medium hover:underline"
          >
            0886399781
          </a>{" "}
          để được hỗ trợ
        </p>

        <button
          onClick={handleTryNow}
          className="mt-6 inline-block bg-yellow-400 text-gray-900 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-yellow-300 transition"
        >
          Định giá ngay
        </button>
      </div>
    </section>
  );
};

// eslint-disable-next-line no-unused-vars
const PassiveIncomeSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);

  const handleTryNow = () => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "passive_income_section" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_button", { location: "passive_income_section" });

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/ai-price";
    } else {
      window.location.href = "/login?returnUrl=/ai-price";
    }
  };

  return (
    <section
      id="passive-income"
      ref={ref}
      className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white py-16"
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          <div
            className={`space-y-6 transform transition-all duration-500 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10"
            }`}
          >
            <h2 className="text-3xl md:text-4xl font-bold">
              Đầu Tư Thông Minh, Tối Ưu Lợi Nhuận
            </h2>
            <p className="text-xl text-blue-100">
              AIPrice không chỉ giúp bạn định giá chính xác mà còn tối ưu hóa
              quyết định đầu tư và gia tăng lợi nhuận
            </p>

            <h3 className="text-2xl font-semibold mt-8 mb-4 text-yellow-300">
              Tối Ưu Hóa Quyết Định Đầu Tư
            </h3>

            <div className="bg-blue-600/30 p-4 rounded-lg border border-blue-400/30 mb-6">
              <p className="text-white mb-2">
                <span className="font-bold text-yellow-300 text-lg">
                  Tăng hiệu quả đầu tư lên đến 150%
                </span>{" "}
                với định giá chính xác và phân tích thị trường thông minh.
              </p>
              <p className="text-blue-100">
                Khách hàng của chúng tôi đã tiết kiệm trung bình 80% thời gian
                định giá và tăng 40% tỷ lệ thành công trong giao dịch.
              </p>
            </div>

            <ul className="space-y-3">
              {passiveIncomeFeatures.map((feature, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-3"
                  style={{ transitionDelay: `${idx * 100 + 300}ms` }}
                >
                  <svg
                    className="w-6 h-6 text-green-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="pt-6">
              <button
                onClick={handleTryNow}
                className="inline-block bg-yellow-400 text-gray-900 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-yellow-300 transition"
              >
                Bắt đầu ngay
              </button>
            </div>
          </div>

          <div
            className={`bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 transform transition-all duration-500 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            }`}
          >
            <h3 className="text-2xl font-bold mb-6 text-center">
              Đăng ký dùng thử miễn phí
            </h3>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-blue-100">
                  Họ và tên
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Nhập họ tên của bạn"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-blue-100">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-blue-100">
                  Số điện thoại
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Nhập số điện thoại"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-blue-100">
                  Gói dịch vụ
                </label>
                <select className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <option value="">Chọn gói dịch vụ</option>
                  <option value="7">Gói 7 ngày - 99.000đ</option>
                  <option value="30">Gói 30 ngày - 249.000đ</option>
                  <option value="180">Gói 180 ngày - 999.000đ</option>
                  <option value="360">Gói 360 ngày - 1.499.000đ</option>
                </select>
              </div>

              <button
                type="button"
                onClick={() => (window.location.href = "/ai-price")}
                className="w-full bg-yellow-400 text-gray-900 py-3 px-4 rounded-lg font-bold text-lg hover:bg-yellow-300 transition"
              >
                Định giá ngay
              </button>

              <p className="text-sm text-blue-200 text-center mt-4">
                Bạn sẽ được dùng thử miễn phí 3 ngày trước khi thanh toán
              </p>

              {/* Security badges */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <p className="text-xs text-blue-200 text-center mb-3">
                  Thanh toán an toàn & bảo mật
                </p>
                <div className="flex justify-center items-center gap-3">
                  <div className="bg-white/20 p-1 rounded">
                    <svg
                      className="w-8 h-8 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
                    </svg>
                  </div>
                  <div className="bg-white/20 p-1 rounded">
                    <svg
                      className="w-8 h-8 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                    </svg>
                  </div>
                  <div className="bg-white/20 p-1 rounded">
                    <svg
                      className="w-8 h-8 text-white"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                    </svg>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => (
  <section className="bg-white py-16">
    <div className="container mx-auto px-4 max-w-4xl">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800">
        Giới thiệu CT Global Holdings
      </h2>
      <ul className="space-y-4 text-lg text-gray-800">
        {companyInfo.map((info, idx) => (
          <li key={idx}>{info}</li>
        ))}
      </ul>
    </div>
  </section>
);

// eslint-disable-next-line no-unused-vars
const FAQSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqs = [
    {
      question: "AIContent có thực sự tạo ra nội dung chất lượng cao không?",
      answer:
        "Có, AIContent sử dụng công nghệ AI tiên tiến để tạo ra nội dung chất lượng cao, tự nhiên và phù hợp với thương hiệu của bạn. Nội dung được tạo ra được tối ưu hóa cho SEO và chuyển đổi.",
    },
    {
      question: "Tôi có cần kỹ năng kỹ thuật để sử dụng AIContent không?",
      answer:
        "Không, AIContent được thiết kế để dễ dàng sử dụng cho mọi người, kể cả những người không có kỹ năng kỹ thuật. Giao diện trực quan và hướng dẫn chi tiết giúp bạn dễ dàng tạo nội dung chất lượng cao.",
    },
    {
      question: "AIContent có hỗ trợ tiếng Việt không?",
      answer:
        "Có, AIContent hỗ trợ đầy đủ tiếng Việt và được tối ưu hóa đặc biệt cho thị trường Việt Nam. Nội dung được tạo ra sẽ tự nhiên, đúng ngữ pháp và phù hợp với văn hóa Việt Nam.",
    },
    {
      question: "Tôi có thể hủy đăng ký bất kỳ lúc nào không?",
      answer:
        "Có, bạn có thể hủy đăng ký bất kỳ lúc nào. Chúng tôi không yêu cầu cam kết dài hạn và bạn có thể hủy dịch vụ trước khi chu kỳ thanh toán tiếp theo bắt đầu.",
    },
    {
      question: "AIContent có giới hạn số lượng nội dung tạo ra không?",
      answer:
        "Mỗi gói dịch vụ có giới hạn khác nhau về số lượng nội dung có thể tạo ra hàng tháng. Bạn có thể nâng cấp gói dịch vụ bất kỳ lúc nào nếu cần tạo nhiều nội dung hơn.",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" ref={ref} className="bg-white py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-blue-700">
          Câu hỏi thường gặp
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className={`border rounded-lg overflow-hidden transition-all duration-300 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <button
                className="flex justify-between items-center w-full p-4 text-left bg-gray-50 hover:bg-gray-100 transition"
                onClick={() => toggleFAQ(idx)}
              >
                <span className="font-medium text-lg text-gray-800">
                  {faq.question}
                </span>
                <svg
                  className={`w-5 h-5 text-blue-600 transform transition-transform ${
                    openIndex === idx ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === idx ? "max-h-40" : "max-h-0"
                }`}
              >
                <div className="p-4 bg-white border-t">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// eslint-disable-next-line no-unused-vars
const ContactSection = () => {
  const [ref, isVisible] = useLazyLoad(0.1);

  return (
    <section
      id="contact"
      ref={ref}
      className="bg-gradient-to-b from-blue-100 to-white py-16"
    >
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-800 text-center">
          Liên hệ với chúng tôi
        </h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10">
          Chúng tôi luôn sẵn sàng hỗ trợ bạn 24/7. Hãy liên hệ với chúng tôi qua
          các kênh sau
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contactInfo.map((c, idx) => (
            <div
              key={idx}
              className={`bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-full flex items-center justify-center w-12 h-12 flex-shrink-0">
                  {c.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">
                    {c.label}
                  </h3>
                  {c.label === "Website" || c.label === "Facebook" ? (
                    <a
                      href={c.value}
                      className="text-blue-600 hover:underline text-sm md:text-base break-words"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {c.value}
                    </a>
                  ) : c.label === "Hotline" || c.label === "Zalo" ? (
                    <a
                      href={`tel:${c.value}`}
                      className="text-blue-600 hover:underline text-sm md:text-base"
                    >
                      {c.value}
                    </a>
                  ) : c.label === "Email" ? (
                    <a
                      href={`mailto:${c.value}`}
                      className="text-blue-600 hover:underline text-sm md:text-base break-words"
                    >
                      {c.value}
                    </a>
                  ) : (
                    <p className="text-gray-600 text-sm md:text-base">
                      {c.value}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-xl font-bold text-blue-800 mb-4">
            Kết nối với chúng tôi
          </h3>
          <div className="flex justify-center gap-6">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 transition"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              className="bg-blue-400 text-white p-3 rounded-full hover:bg-blue-500 transition"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.054 10.054 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
              </svg>
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              className="bg-red-600 text-white p-3 rounded-full hover:bg-red-700 transition"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21.582 7.172a2.513 2.513 0 00-1.768-1.768C18.254 5 12 5 12 5s-6.254 0-7.814.404a2.513 2.513 0 00-1.768 1.768C2.014 8.732 2 12 2 12s.014 3.268.418 4.828c.23.857.911 1.538 1.768 1.768C5.746 19 12 19 12 19s6.254 0 7.814-.404a2.513 2.513 0 001.768-1.768C22 15.268 22 12 22 12s0-3.268-.418-4.828zM10 15V9l5.2 3-5.2 3z" />
              </svg>
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              className="bg-green-600 text-white p-3 rounded-full hover:bg-green-700 transition"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer id="contact" className="bg-[#14389F] text-white py-12">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div className="flex flex-col items-center">
          <h3 className="text-lg font-bold mb-4 text-center">Sản phẩm</h3>
          <ul className="space-y-2 text-blue-200 text-center w-full">
            <li>
              <a href="/ai-price" className="hover:text-white transition">
                AIPrice
              </a>
            </li>
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="hover:text-white transition">
                AIContent
              </a>
            </li>
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="hover:text-white transition">
                AIVideo
              </a>
            </li>
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="hover:text-white transition">
                AIToolFB
              </a>
            </li>
          </ul>
        </div>

        <div className="flex flex-col items-center">
          <h3 className="text-lg font-bold mb-4 text-center">Hỗ trợ</h3>
          <ul className="space-y-2 text-blue-200 text-center w-full">
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="hover:text-white transition">
                Hướng dẫn sử dụng
              </a>
            </li>
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="hover:text-white transition">
                Câu hỏi thường gặp
              </a>
            </li>
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="hover:text-white transition">
                Liên hệ hỗ trợ
              </a>
            </li>
            <li>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="hover:text-white transition">
                Chính sách bảo mật
              </a>
            </li>
          </ul>
        </div>

        <div className="flex flex-col items-center">
          <h3 className="text-lg font-bold mb-4 text-center">Liên hệ</h3>
          <ul className="space-y-2 text-blue-200 text-center w-full">
            <li className="flex items-center justify-center">
              <FiPhone className="mr-2" /> 0886399781
            </li>
            <li className="flex items-center justify-center">
              <FiMessageCircle className="mr-2" /> contact@youhomes.vn
            </li>
            <li className="flex items-center justify-center">
              <FiGlobe className="mr-2" /> youhomes.vn/aiprice
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-blue-800 pt-6 mt-6 text-center">
        <p>
          © {new Date().getFullYear()} AIPrice – Công cụ định giá bất động sản
          tự động bằng AI. Một sản phẩm của CT Global Holdings.
        </p>
        <div className="mt-4 flex justify-center space-x-4">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" className="text-blue-300 hover:text-white transition">
            Chính sách bảo mật
          </a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" className="text-blue-300 hover:text-white transition">
            Điều khoản sử dụng
          </a>
          {/* <a href="#" className="text-blue-300 hover:text-white transition">
            Sitemap
          </a> */}
        </div>
      </div>
    </div>
  </footer>
);

const StickyCTA = () => {
  const [isVisible, setIsVisible] = React.useState(false);

  const handleTryNow = () => {
    // Track the click event with Meta Pixel
    trackEvent("TryNowClick", { button_location: "sticky_cta" });

    // Also track with Firebase Analytics
    trackButtonClick("try_now_button", { location: "sticky_cta" });

    const userAccessToken = localStorage.getItem("userAccessToken");
    if (userAccessToken) {
      window.location.href = "/ai-price";
    } else {
      window.location.href = "/login?returnUrl=/ai-price";
    }
  };

  React.useEffect(() => {
    const handleScroll = () => {
      // Hiển thị nút khi người dùng cuộn xuống 500px
      if (window.scrollY > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 transform ${
        isVisible
          ? "translate-y-0 opacity-100"
          : "translate-y-10 opacity-0 pointer-events-none"
      }`}
    >
      <button
        onClick={handleTryNow}
        className="bg-yellow-400 text-gray-900 px-4 py-3 rounded-full font-bold shadow-lg hover:bg-yellow-300 transition flex items-center gap-2"
      >
        <span>Định giá ngay</span>
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

const LandingPage2 = () => {
  // Thêm useEffect để scroll đến phần tương ứng khi URL có hash
  React.useEffect(() => {
    // Add smooth scroll behavior to html element
    document.documentElement.style.scrollBehavior = "smooth";

    // Xử lý scroll đến phần tương ứng khi URL có hash
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          // Thêm một chút delay để đảm bảo trang đã render xong
          setTimeout(() => {
            window.scrollTo({
              top: element.offsetTop - 80, // Trừ đi chiều cao của header
              behavior: "smooth",
            });
          }, 100);
        }
      }
    };

    // Xử lý khi trang được load
    handleHashChange();

    // Xử lý khi hash thay đổi
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen scroll-smooth">
      <SEO />
      <Header />
      <main className="relative">
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <FeaturesSection />
        <TargetUsersSection />
        {/* <ClientLogos /> */}
        <TestimonialsSection />
        <ProductivitySection />
        {/* <PassiveIncomeSection /> */}
        {/* <FAQSection /> */}
        <AboutSection />
        {/* <ContactSection /> */}
      </main>
      <Footer />
      <StickyCTA />
    </div>
  );
};

export default LandingPage2;
